import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import {
  ClientDocument,
  downloadDocument,
  formatFileSize,
  listDocuments,
  removeDocument,
  uploadMsa,
} from '../services/clientDocumentService';

interface ClientDocumentsProps {
  clientId: string;
  /**
   * Whether to draw the upload and remove controls. This decides what is shown, not what is
   * permitted — the API enforces the real rule against the caller's own claims, so a reader
   * who forced this to true would still be refused.
   */
  canManage?: boolean;
}

const MSA_LABEL = 'Master Services Agreement';

/**
 * The signed agreements held against a client organization.
 *
 * Used in two places with different powers: a client administrator sees their own
 * organization's agreement and can download it, while a system administrator can also upload,
 * replace and remove.
 */
const ClientDocuments: React.FC<ClientDocumentsProps> = ({ clientId, canManage = false }) => {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDocuments(await listDocuments(clientId));
    } catch {
      setError('Could not load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) load();
  }, [clientId, load]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input immediately, or choosing the same file twice in a row fires no change
    // event and the second upload silently never happens.
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setError('');
    setNotice('');
    try {
      await uploadMsa(clientId, file);
      setNotice(`${file.name} uploaded.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: ClientDocument) => {
    setBusyId(doc.id);
    setError('');
    try {
      await downloadDocument(clientId, doc.id, doc.fileName);
    } catch {
      setError('Could not download the document.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (doc: ClientDocument) => {
    // Removing an executed contract from a client's view deserves a deliberate confirmation.
    if (!window.confirm(`Remove "${doc.fileName}"? The client will no longer see it.`)) return;

    setBusyId(doc.id);
    setError('');
    setNotice('');
    try {
      await removeDocument(clientId, doc.id);
      setNotice('Document removed.');
      await load();
    } catch {
      setError('Could not remove the document.');
    } finally {
      setBusyId(null);
    }
  };

  const msa = documents.find((d) => d.documentType === 'MSA');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#0f172a]">Documents</h2>
          <p className="text-sm text-[#64748b] mt-1">
            The signed agreement on file for this organization.
          </p>
        </div>

        {canManage && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:opacity-60 text-white font-medium transition-all flex items-center space-x-2 shadow-lg shadow-green-500/20"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span>{msa ? 'Replace MSA' : 'Upload signed MSA'}</span>
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#c41e3a]" />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-10">
          <FileText className="w-10 h-10 text-[#cbd5e1] mx-auto mb-3" />
          <p className="text-[#64748b]">
            {canManage
              ? 'No signed agreement on file yet.'
              : 'No signed agreement is on file for your organization yet.'}
          </p>
          {!canManage && (
            <p className="text-sm text-[#94a3b8] mt-1">
              Contact AutoAccess Solutions if you believe this is wrong.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-4 rounded-xl border border-black/[0.08] bg-white"
            >
              <div className="flex items-center space-x-4 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#c41e3a]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#c41e3a]" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[#0f172a] truncate">{doc.fileName}</p>
                  <p className="text-sm text-[#64748b]">
                    {doc.documentType === 'MSA' ? MSA_LABEL : doc.documentType}
                    {' · '}
                    {formatFileSize(doc.sizeBytes)}
                    {' · '}
                    Uploaded {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                    {doc.uploadedByName ? ` by ${doc.uploadedByName}` : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={busyId === doc.id}
                  className="px-3 py-2 rounded-xl border border-black/[0.08] hover:bg-black/[0.03] disabled:opacity-60 text-[#0f172a] font-medium transition-all flex items-center space-x-2"
                  title={`Download ${doc.fileName}`}
                >
                  {busyId === doc.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Download</span>
                </button>

                {canManage && (
                  <button
                    onClick={() => handleRemove(doc)}
                    disabled={busyId === doc.id}
                    className="p-2 rounded-xl text-[#64748b] hover:text-red-600 hover:bg-red-50 disabled:opacity-60 transition-all"
                    title={`Remove ${doc.fileName}`}
                    aria-label={`Remove ${doc.fileName}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <p className="text-xs text-[#94a3b8] mt-6">
          PDF only, up to 10 MB. Uploading a new agreement supersedes the one on file — the
          previous copy is retained for audit and is no longer shown to the client.
        </p>
      )}
    </div>
  );
};

export default ClientDocuments;
